import { Title, LegacyGrid as Grid } from '../../elements';
import { useTranslation } from 'react-i18next';
import { Chip, Icon, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { V4_TABLE } from '../../theme/v4Compat';
import { AnimalSlaughterhouseData } from '../../models/animal-view';

interface AnimalViewSlaughterhouseProps {
  slaughterhouseData: AnimalSlaughterhouseData;
  showTitle?: boolean;
  title?: string;
}

const AnimalViewSlaughterhouse = ({ slaughterhouseData, showTitle = false, title }: AnimalViewSlaughterhouseProps) => {
  const { t } = useTranslation();

  const renderMoneyCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (value > 0) {
      return `${value.toFixed(1)} (EURO)`;
    }

    return '-';
  };

  const renderWeightCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return 'N/A (kg)';
    }

    if (value > 0) {
      return `${value.toFixed(1)} (kg)`;
    }

    return 'N/A (kg)';
  };

  const renderSizeCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return 'N/A (mm)';
    }

    if (value > 0) {
      return `${value.toFixed(1)} (mm)`;
    }

    return 'N/A (mm)';
  };

  const renderPercentsCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (value > 0) {
      return `${value.toFixed(1)}%`;
    }

    return 'N/A';
  };

  return (
    <>
      <Grid item container justifyContent="space-between" alignItems="center" spacing={2}>
        <Grid item>
          {showTitle && (
            <Grid container direction="row" spacing={1} alignItems="center">
              <Grid item>
                <Icon color="primary" fontSize="large">
                  {'list'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{title ?? t('f-slaughterhouse-data')}</Title>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container direction="row" spacing={1}>
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item md={12}>
              <Table size="small" sx={V4_TABLE} aria-label="animal data table" id="animal-data-table">
                <TableBody>
                  {slaughterhouseData.timestamp && (
                    <TableRow key={'slaughterhouseTimestamp'}>
                      <TableCell align="right">{t('f-com-date-time')}</TableCell>
                      <TableCell sx={BOLD}>
                        {new Date(slaughterhouseData.timestamp ?? new Date()).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow key={'slaughterMoney'}>
                    <TableCell align="right">{t('f-slaughterhouse-money')}</TableCell>
                    <TableCell sx={BOLD}>{renderMoneyCell(slaughterhouseData.money)}</TableCell>
                  </TableRow>

                  {slaughterhouseData.hookId && (
                    <TableRow key={'slaughterhouseNumber'}>
                      <TableCell align="right">{t('f-slaughterhouse-hook-number')}</TableCell>
                      <TableCell sx={BOLD}>{slaughterhouseData.hookId}</TableCell>
                    </TableRow>
                  )}

                  {slaughterhouseData.tradingClass && (
                    <TableRow key={'slaughterhouseTradingClass'}>
                      <TableCell align="right">{t('f-slaughterhouse-trading-class')}</TableCell>
                      <TableCell sx={BOLD}>{slaughterhouseData.tradingClass}</TableCell>
                    </TableRow>
                  )}

                  <TableRow key={'slaughterhouseWeight'}>
                    <TableCell align="right">{t('f-slaughterhouse-weight')}</TableCell>
                    <TableCell sx={BOLD}>{renderWeightCell(slaughterhouseData.weight)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseBaconCutlet'}>
                    <TableCell align="right">{t('f-slaughterhouse-bacon-cutlet')}</TableCell>
                    <TableCell sx={BOLD}>{renderSizeCell(slaughterhouseData.baconCutlet)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseFat'}>
                    <TableCell align="right">{t('f-slaughterhouse-fat')}</TableCell>
                    <TableCell sx={BOLD}>{renderSizeCell(slaughterhouseData.fat)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseLeanMeat'}>
                    <TableCell align="right">{t('f-slaughterhouse-lean-meat')}</TableCell>
                    <TableCell sx={BOLD}>{renderPercentsCell(slaughterhouseData.leanMeat)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseBelly'}>
                    <TableCell align="right">{t('f-slaughterhouse-total-weight-belly')}</TableCell>
                    <TableCell sx={BOLD}>{renderWeightCell(slaughterhouseData.belly)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseHam'}>
                    <TableCell align="right">{t('f-slaughterhouse-ham')}</TableCell>
                    <TableCell sx={BOLD}>{renderWeightCell(slaughterhouseData.ham)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseSpine'}>
                    <TableCell align="right">{t('f-slaughterhouse-spine')}</TableCell>
                    <TableCell sx={BOLD}>{renderWeightCell(slaughterhouseData.spine)}</TableCell>
                  </TableRow>

                  <TableRow key={'slaughterhouseShoulder'}>
                    <TableCell align="right">{t('f-slaughterhouse-shoulder')}</TableCell>
                    <TableCell sx={BOLD}>{renderWeightCell(slaughterhouseData.shoulder)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={1}>
            {(slaughterhouseData.problems ?? []).length > 0 && (
              <Grid item>
                <Title>{t('f-slaughterhouse-problems')}:</Title>
              </Grid>
            )}
            {(slaughterhouseData.problems ?? []).map((problem, index) => (
              <Grid item key={index}>
                <Chip label={problem} color="secondary" variant="outlined" />
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AnimalViewSlaughterhouse;

const BOLD = { fontWeight: 'bold' } as const;
